import React, { useCallback, useEffect, useMemo } from 'react';
import { useObservable } from 'react-use-observable';

import { Form, Select } from 'antd';

import { useFormikContext } from 'formik';
import type { IChildren, ISelectProduct } from 'v2/interfaces/product/selectProduct';
import productService from 'v2/services/selectProduct';

interface IProps {
  endpoint?: string;
  disabled?: boolean;
  inputClearButton?: boolean;
  label?: React.ReactNode;
  onConfirm?: (product_id: number) => void;
  layout?: 'vertical' | 'horizontal';
}

export const SingleSelectProduct = ({
  label,
  endpoint,
  inputClearButton,
  disabled,
  onConfirm: onConfirmProp,
  layout = 'vertical'
}: IProps) => {
  const { setFieldValue, values } = useFormikContext<{ product_id: number | string | null }>();
  const [isLoadingProducts] = useObservable(() => productService.isLoadingProducts(), []);

  useEffect(() => {
    return () => {
      productService.setProducts([]);
    };
  }, []);

  const [products] = useObservable(() => productService.loadDeliveries(endpoint), [endpoint]);

  const options = useMemo(() => {
    const items: Array<{ value: number; label: string }> = [];

    (products || []).forEach((product: ISelectProduct) => {
      items.push({ value: product.id, label: `${product.id} - ${product.title}` });

      (product.children || []).forEach((child: IChildren) => {
        items.push({ value: child.id, label: `${child.id} - ${child.title}` });
      });
    });

    return Array.from(new Map(items.map(item => [item.value, item])).values());
  }, [products]);

  const selectedValue = values.product_id != null && values.product_id !== '' ? Number(values.product_id) : undefined;

  const selectValue = selectedValue != null && !Number.isNaN(selectedValue) ? selectedValue : undefined;

  const hasOptionForValue = selectValue != null && options.some(option => option.value === selectValue);

  const displayValue = hasOptionForValue ? selectValue : undefined;

  const placeholder =
    isLoadingProducts || (selectValue != null && !hasOptionForValue)
      ? 'Carregando seus produtos...'
      : 'Selecione um produto';

  const onChange = useCallback(
    (productId?: number) => {
      const nextProductId = productId ?? null;
      setFieldValue('product_id', nextProductId);

      if (onConfirmProp && typeof nextProductId === 'number') {
        onConfirmProp(nextProductId);
      }
    },
    [onConfirmProp, setFieldValue]
  );

  return (
    <Form.Item className='mb-0!' label={label} layout={layout}>
      <Select
        id='single-products'
        className='mt-2 w-full'
        showSearch
        allowClear={inputClearButton}
        disabled={disabled}
        loading={isLoadingProducts}
        value={displayValue}
        placeholder={placeholder}
        options={options}
        notFoundContent={isLoadingProducts ? 'Carregando produtos...' : 'Nenhum produto encontrado'}
        filterOption={(input, option) => {
          const label = String(option?.label ?? '').toLowerCase();
          const value = String(option?.value ?? '').toLowerCase();
          const term = input.toLowerCase();

          return label.includes(term) || value.includes(term);
        }}
        onChange={value => onChange(value as number | undefined)}
      />
    </Form.Item>
  );
};
